/**
 * Growth Page
 *
 * Platform growth metrics: new orgs, users, groups over time.
 * Shows monthly and weekly trends.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

export default function GrowthPage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getGrowth();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[GrowthPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading growth data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const trends = data?.trends || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Growth Metrics</h2>
          <p className="text-gray-500 mt-1">
            New organizations, members, and groups over time
          </p>
        </div>
        <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
      </div>

      {/* This Month KPIs */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">This Month</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="New Organizations"
            value={metrics.newOrganizationsThisMonth}
            loading={loading}
            icon={BuildingOfficeIcon}
          />
          <KPICard
            title="New Members"
            value={metrics.newMembersThisMonth}
            loading={loading}
            icon={UsersIcon}
          />
          <KPICard
            title="New Groups"
            value={metrics.newGroupsThisMonth}
            loading={loading}
            icon={UserGroupIcon}
          />
        </div>
      </section>

      {/* Monthly Trends */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Monthly Trends (Last 6 Months)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organizations by Month */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Organizations</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={(trends.organizationsByMonth || []).map((t) => ({
                  date: t.month,
                  count: t.count,
                }))}
                lines={[{ key: 'count', name: 'New Orgs', color: '#3B82F6' }]}
                xAxisKey="date"
                height={200}
              />
            )}
          </div>

          {/* Members by Month */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Members</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={(trends.membersByMonth || []).map((t) => ({
                  date: t.month,
                  count: t.count,
                }))}
                lines={[{ key: 'count', name: 'New Members', color: '#10B981' }]}
                xAxisKey="date"
                height={200}
              />
            )}
          </div>

          {/* Groups by Month */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Groups</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={(trends.groupsByMonth || []).map((t) => ({
                  date: t.month,
                  count: t.count,
                }))}
                lines={[{ key: 'count', name: 'New Groups', color: '#8B5CF6' }]}
                xAxisKey="date"
                height={200}
              />
            )}
          </div>
        </div>
      </section>

      {/* Weekly Trends */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Weekly Trends (Last 8 Weeks)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weekly Organizations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Weekly New Organizations</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={(trends.weeklyOrganizations || []).map((t) => ({
                  date: t.week,
                  count: t.count,
                }))}
                lines={[{ key: 'count', name: 'Orgs', color: '#3B82F6' }]}
                xAxisKey="date"
                height={200}
              />
            )}
          </div>

          {/* Weekly Members */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Weekly New Members</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={(trends.weeklyMembers || []).map((t) => ({
                  date: t.week,
                  count: t.count,
                }))}
                lines={[{ key: 'count', name: 'Members', color: '#10B981' }]}
                xAxisKey="date"
                height={200}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
